import type { ReactNode } from 'react'
import cx from 'classnames'
import { format } from 'date-fns'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import {
  Skeleton,
  SkeletonList,
  useSkeletonListContext,
} from '@/components/Skeleton'
import { displayTitle } from '@/features/jobs/details/display-title'
import { PaneHeader } from '@/features/jobs/details/components/JobSections/Pane'
import { NoteCardSkeleton } from '@/features/jobs/details/components/JobSections/NoteCard'
import {
  type DocumentActions,
  DocumentItemActionsDropdown,
} from '@/features/documents/components/DocumentItemActionsDropdown'
import styles from './OverviewPane.module.css'
import { type ChecklistRow } from '@/features/jobs/details/components/JobSections/OverviewPane/OverviewPaneViewModel'

export interface OverviewPaneProps {
  children: ReactNode
}

/**
 * The job at a glance: what the client still owes, what just came in, and
 * what the team has noted about it.
 */
export function OverviewPane({ children }: OverviewPaneProps) {
  return (
    <div className={styles.root} data-test="job-overview">
      {children}
    </div>
  )
}

export interface ClientChecklistProps {
  rows: ChecklistRow[]
  isSkeleton: boolean
}

export function ClientChecklist({ rows, isSkeleton }: ClientChecklistProps) {
  const done = rows.filter((r) => r.done).length

  return (
    <section data-test="overview-client-checklist">
      <PaneHeader
        title="Client checklist"
        count={
          !isSkeleton && rows.length > 0
            ? `${done} of ${rows.length}`
            : undefined
        }
      />
      <div className={styles.checklist}>
        {isSkeleton ? (
          <SkeletonList count={4}>
            <ChecklistRowSkeleton />
          </SkeletonList>
        ) : rows.length === 0 ? (
          <div className={styles.quiet}>Nothing requested or uploaded yet.</div>
        ) : (
          rows.map((row) => (
            <div key={row.key} className={styles.checkRow}>
              {row.done ? <DoneIcon /> : <MissingIcon />}
              <span
                className={cx(styles.checkName, !row.done && styles.missing)}
              >
                {row.name}
              </span>
              <span className={styles.via}>{row.via}</span>
            </div>
          ))
        )}
      </div>
    </section>
  )
}

function ChecklistRowSkeleton() {
  const { varyPercent } = useSkeletonListContext()
  return (
    <div className={cx(styles.checkRow, styles.skeleton)} aria-hidden>
      <Skeleton variant="circle" width={14} height={14} />
      <span className={styles.checkName}>
        <Skeleton width={varyPercent(70)} />
      </span>
      <span className={styles.via}>
        <Skeleton width={varyPercent(25)} />
      </span>
    </div>
  )
}

export interface RecentUploadsProps {
  documentCount: number
  hasDocuments: boolean
  isSkeleton: boolean
  isEmpty: boolean
  onViewAll: () => unknown
  txfButton?: ReactNode
  children: ReactNode
}

export function RecentUploads({
  documentCount,
  hasDocuments,
  isSkeleton,
  isEmpty,
  onViewAll,
  txfButton,
  children,
}: RecentUploadsProps) {
  return (
    <section>
      <PaneHeader
        title="Recent uploads"
        count={documentCount > 0 ? String(documentCount) : undefined}
      >
        {txfButton}
        {hasDocuments && (
          <RawButton className={styles.link} onPress={onViewAll}>
            View all
          </RawButton>
        )}
      </PaneHeader>
      {isSkeleton ? (
        <SkeletonList count={3}>
          <UploadRowSkeleton />
        </SkeletonList>
      ) : isEmpty ? (
        <div className={cx(styles.quiet, styles.ruled)}>
          Nothing uploaded yet.
        </div>
      ) : (
        <div className={styles.uploads}>{children}</div>
      )}
    </section>
  )
}

export interface UploadRowProps {
  title: string
  isProcessing: boolean
  documentTypeName?: string
  ownerName: string
  dateCreated: Date
  onPress: () => unknown
  /**
   * `null` hides the actions dropdown.
   */
  actions: DocumentActions | null
}

export function UploadRow({
  title,
  isProcessing,
  documentTypeName,
  ownerName,
  dateCreated,
  onPress,
  actions,
}: UploadRowProps) {
  return (
    <div className={styles.upload}>
      <RawButton className={styles.uploadMain} onPress={onPress}>
        <span className={styles.uploadText}>
          <span className={styles.uploadName}>{displayTitle(title)}</span>
          <span className={styles.uploadType}>
            {isProcessing ? 'Processing…' : (documentTypeName ?? 'Other')}
          </span>
        </span>
        <span className={styles.uploadBy}>{ownerName}</span>
        <span className={styles.uploadDate}>
          {format(dateCreated, 'MMM d')}
        </span>
      </RawButton>
      <span className={styles.uploadAction}>
        {actions && (
          <DocumentItemActionsDropdown
            actions={actions}
            variant="ghost"
            title={title}
          />
        )}
      </span>
    </div>
  )
}

function UploadRowSkeleton() {
  const { varyPercent } = useSkeletonListContext()
  return (
    <div className={cx(styles.upload, styles.skeleton)} aria-hidden>
      <span className={styles.uploadMain}>
        <span className={styles.uploadText}>
          <span className={styles.uploadName}>
            <Skeleton width={varyPercent(50)} />
          </span>
          <span className={styles.uploadType}>
            <Skeleton width={varyPercent(20)} />
          </span>
        </span>
        <span className={styles.uploadBy}>
          <Skeleton width={varyPercent(70)} />
        </span>
        <span className={styles.uploadDate}>
          <Skeleton width={36} />
        </span>
      </span>
      <span className={styles.uploadAction} />
    </div>
  )
}

export interface RecentNotesProps {
  canWrite: boolean
  onAddNote: () => unknown
  noteCount: number
  isSkeleton: boolean
  countLabel?: string
  onViewAll: () => unknown
  children: ReactNode
}

export function RecentNotes({
  canWrite,
  onAddNote,
  noteCount,
  isSkeleton,
  countLabel,
  onViewAll,
  children,
}: RecentNotesProps) {
  return (
    <section className={styles.notes}>
      <PaneHeader
        title="Recent job notes"
        meta={
          <span className={styles.private}>
            <Icon name="lock-outline" size={12} aria-hidden="true" />
            Not visible to the client
          </span>
        }
      >
        {canWrite && (
          <RawButton className={styles.button} onPress={onAddNote}>
            <Icon name="add" size={12} aria-hidden="true" />
            Add note
          </RawButton>
        )}
      </PaneHeader>
      {isSkeleton ? (
        <div className={styles.noteList}>
          <SkeletonList count={2}>
            <NoteCardSkeleton />
          </SkeletonList>
        </div>
      ) : noteCount === 0 ? (
        <div className={styles.quiet}>No notes on this job yet.</div>
      ) : (
        <div className={cx(styles.noteList, styles.scrolls)}>{children}</div>
      )}
      {noteCount > 2 && (
        <RawButton
          className={cx(styles.link, styles.viewAll)}
          onPress={onViewAll}
        >
          View all {countLabel} notes
        </RawButton>
      )}
    </section>
  )
}

function DoneIcon() {
  return (
    <Icon name="status-check" size={14} color="var(--ok)" aria-hidden="true" />
  )
}

function MissingIcon() {
  return (
    <Icon
      name="status-dashed"
      size={14}
      color="var(--warn)"
      aria-hidden="true"
    />
  )
}
